import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { BookOpen, GraduationCap, User } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { apiUrl } from '../config/api';
import { consumePendingLibraryAdd } from './AddToLibrary';

export default function Login() {
  const navigate = useNavigate();
  const { loginStudent, loginTeacher } = useAuth();
  const { toast } = useToast();

  // Student login state - three words as array
  const [words, setWords] = useState(['', '', '']);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState('');

  // Refs for auto-advancing between inputs
  const wordRefs = [useRef(null), useRef(null), useRef(null)];

  // Teacher login state
  const [teacherEmail, setTeacherEmail] = useState('');
  const [teacherPassword, setTeacherPassword] = useState('');
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [teacherError, setTeacherError] = useState('');

  // Handle word input — only allow a-z
  const handleWordChange = (index, value) => {
    const filtered = value.toLowerCase().replace(/[^a-z]/g, '');
    setWords(prev => {
      const next = [...prev];
      next[index] = filtered;
      return next;
    });
    setStudentError('');
  };

  // Handle key down for auto-advance (space, enter, comma)
  const handleWordKeyDown = (e, index) => {
    const isLastField = index === 2;
    const currentValue = words[index];

    if (e.key === ' ' || e.key === 'Enter' || e.key === ',') {
      if (isLastField) {
        if (e.key === 'Enter' && words[0] && words[1] && currentValue) {
          // Let the form submit naturally
          return;
        }
        e.preventDefault();
        return;
      }

      e.preventDefault();
      if (currentValue.length > 0) {
        wordRefs[index + 1].current?.focus();
      }
    }

    // Allow backspace to previous field when empty
    if (e.key === 'Backspace' && currentValue.length === 0 && index > 0) {
      e.preventDefault();
      wordRefs[index - 1].current?.focus();
    }
  };

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    setStudentError('');

    if (!words[0] || !words[1] || !words[2]) {
      setStudentError('Please enter all three words of your access code');
      return;
    }

    const studentCode = words.join('-');
    setStudentLoading(true);

    try {
      await loginStudent(studentCode);
      toast({
        title: "Welcome!",
        description: "You've successfully logged in.",
      });
      const pendingBookId = consumePendingLibraryAdd();
      navigate(pendingBookId ? `/add/${pendingBookId}` : '/home');
    } catch (error) {
      setStudentError(error.message || 'Invalid access code');
    } finally {
      setStudentLoading(false);
    }
  };

  const handleTeacherLogin = async (e) => {
    e.preventDefault();
    setTeacherError('');

    if (!teacherEmail || !teacherPassword) {
      setTeacherError('Please enter both email and password');
      return;
    }

    setTeacherLoading(true);

    try {
      await loginTeacher(teacherEmail, teacherPassword);
      toast({
        title: "Welcome back!",
        description: "You've successfully logged in.",
      });
      const pendingBookId = consumePendingLibraryAdd();
      navigate(pendingBookId ? `/add/${pendingBookId}` : '/home');
    } catch (error) {
      setTeacherError(error.message || 'Login failed');
    } finally {
      setTeacherLoading(false);
    }
  };

  const handleSSO = async (provider) => {
    try {
      const res = await fetch(apiUrl('/api/auth/sso/sign-in/social'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider,
          callbackURL: `${window.location.origin}/auth/callback`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      toast({
        title: "Error",
        description: `Failed to initiate ${provider} sign-in. Please try again.`,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <BookOpen className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-serif">Welcome to Readwell</CardTitle>
          <CardDescription>
            Sign in to access your audiobooks and readings
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="student" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="student" className="gap-2">
                <GraduationCap className="h-4 w-4" />
                Student
              </TabsTrigger>
              <TabsTrigger value="teacher" className="gap-2">
                <User className="h-4 w-4" />
                Teacher
              </TabsTrigger>
            </TabsList>

            {/* Student Login Tab */}
            <TabsContent value="student">
              <form onSubmit={handleStudentLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label id="access-code-label">Your Access Code</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {words.map((word, i) => (
                      <div key={i}>
                        <span id={`access-code-word-${i + 1}-label`} className="sr-only">
                          Word {i + 1} of 3
                        </span>
                        <Input
                          ref={wordRefs[i]}
                          id={`access-code-word-${i + 1}`}
                          type="text"
                          placeholder={`word ${i + 1}`}
                          value={word}
                          onChange={(e) => handleWordChange(i, e.target.value)}
                          onKeyDown={(e) => handleWordKeyDown(e, i)}
                          disabled={studentLoading}
                          className="text-center font-mono lowercase"
                          autoComplete="off"
                          maxLength={20}
                          autoFocus={i === 0}
                          aria-labelledby={`access-code-label access-code-word-${i + 1}-label`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Example: astronaut - dancing - pizza
                  </p>
                </div>

                {studentError && (
                  <Alert variant="destructive">
                    <AlertDescription>{studentError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={studentLoading || !words[0] || !words[1] || !words[2]}
                >
                  {studentLoading ? 'Signing in...' : 'Sign In as Student'}
                </Button>
              </form>
            </TabsContent>

            {/* Teacher Login Tab */}
            <TabsContent value="teacher">
              <form onSubmit={handleTeacherLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="teacher-email">Email</Label>
                  <Input
                    id="teacher-email"
                    type="email"
                    placeholder="teacher@school.edu"
                    value={teacherEmail}
                    onChange={(e) => {
                      setTeacherEmail(e.target.value);
                      setTeacherError('');
                    }}
                    disabled={teacherLoading}
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="teacher-password">Password</Label>
                  <Input
                    id="teacher-password"
                    type="password"
                    placeholder="Enter your password"
                    value={teacherPassword}
                    onChange={(e) => {
                      setTeacherPassword(e.target.value);
                      setTeacherError('');
                    }}
                    disabled={teacherLoading}
                    autoComplete="current-password"
                  />
                </div>

                {teacherError && (
                  <Alert variant="destructive">
                    <AlertDescription>{teacherError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={teacherLoading}
                >
                  {teacherLoading ? 'Signing in...' : 'Sign In with Email'}
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSSO('google')}
                    disabled={teacherLoading}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Google
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSSO('microsoft')}
                    disabled={teacherLoading}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                      <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                      <path fill="#f35325" d="M1 1h10v10H1z" />
                      <path fill="#81bc06" d="M12 1h10v10H12z" />
                      <path fill="#05a6f0" d="M1 12h10v10H1z" />
                      <path fill="#ffba08" d="M12 12h10v10H12z" />
                    </svg>
                    Microsoft
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
